import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Facebook,
  Plug,
  FileText,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
  MousePointerClick,
  DollarSign,
  TrendingUp,
  Megaphone,
  Layers,
  Image,
  Clock,
  Unplug,
  ToggleLeft,
  ToggleRight,
  ArrowRightLeft,
  Save,
  ExternalLink,
  Calendar,
  Target,
  Users,
  Wallet,
} from 'lucide-react'
import { metaApi, metaAnalyticsApi } from '../../api/endpoints'
import { fmtDateTime } from '../../utils/date'
import Button from '../../components/ui/Button'
import Input, { Select } from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import Modal from '../../components/ui/Modal'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = 'connection' | 'forms' | 'campaigns'

type ConnectionData = {
  id?: number
  app_id?: string
  access_token?: string
  ad_account_id?: string
  ad_account_name?: string
  page_id?: string
  page_name?: string
  status?: string
  ads_tracking_enabled?: boolean
  token_expires_at?: string | null
  created_at?: string
} | null

const SYSTEM_FIELDS = [
  { value: '', label: '-- Skip --' },
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone_number', label: 'Phone Number' },
  { value: 'company', label: 'Company' },
  { value: 'title', label: 'Title' },
  { value: 'source', label: 'Source' },
  { value: 'notes', label: 'Notes' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'country', label: 'Country' },
]

const SYNC_INTERVALS = [
  { value: '15', label: 'Every 15 minutes' },
  { value: '30', label: 'Every 30 minutes' },
  { value: '60', label: 'Every hour' },
  { value: '120', label: 'Every 2 hours' },
  { value: '360', label: 'Every 6 hours' },
  { value: '720', label: 'Every 12 hours' },
  { value: '1440', label: 'Every 24 hours' },
  { value: '', label: 'Disabled' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: unknown): string {
  if (n == null) return '0'
  return Number(n).toLocaleString()
}

function fmtCurrency(n: unknown): string {
  if (n == null) return '\u20B90.00'
  return `\u20B9${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtPct(n: unknown): string {
  if (n == null) return '0.00%'
  return `${Number(n).toFixed(2)}%`
}

function fmtDate(d: unknown): string {
  if (!d) return 'Never'
  return fmtDateTime(d as string)
}

function campaignStatusVariant(status: string): 'success' | 'warning' | 'default' | 'danger' {
  const s = (status || '').toUpperCase()
  if (s === 'ACTIVE') return 'success'
  if (s === 'PAUSED') return 'warning'
  if (s === 'DELETED' || s === 'ARCHIVED') return 'danger'
  return 'default'
}

function fmtShortDate(d: unknown): string {
  if (!d) return ''
  const date = new Date(d as string)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

function daysBetween(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return ''
  const s = new Date(start)
  const e = end ? new Date(end) : new Date()
  if (isNaN(s.getTime())) return ''
  const days = Math.max(0, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)))
  if (!end) return `${days} days running`
  return `${days} days`
}

// ---------------------------------------------------------------------------
// Spend Sparkline — CSS bar chart of daily values
// ---------------------------------------------------------------------------

function SpendSparkline({ data, valueKey = 'spend' }: { data: any[]; valueKey?: string }) {
  if (!data || data.length === 0) return null

  // Take last 28 days max
  const sliced = data.slice(-28)
  const values = sliced.map((d: any) => Number(d[valueKey]) || 0)
  const maxVal = Math.max(...values, 0.01) // avoid divide by zero

  return (
    <div className="mt-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
        Daily Spend — Last {sliced.length} days
      </p>
      <div className="flex items-end gap-px h-12">
        {sliced.map((d: any, i: number) => {
          const val = values[i]
          const heightPct = Math.max((val / maxVal) * 100, 2) // minimum 2% so zero days are still visible
          const dateLabel = fmtShortDate(d.date)
          return (
            <div
              key={i}
              className="flex-1 min-w-[3px] max-w-[12px] rounded-t bg-indigo-400 hover:bg-indigo-600 transition-colors cursor-default group relative"
              style={{ height: `${heightPct}%` }}
              title={`${dateLabel}: ${fmtCurrency(val)}`}
            >
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 pointer-events-none">
                <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-lg">
                  {dateLabel}: {fmtCurrency(val)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-gray-400">{fmtShortDate(sliced[0]?.date)}</span>
        <span className="text-[10px] text-gray-400">{fmtShortDate(sliced[sliced.length - 1]?.date)}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Budget Progress Bar
// ---------------------------------------------------------------------------

function BudgetProgress({ spent, total }: { spent: number; total: number }) {
  if (!total || total <= 0) return null
  const pct = Math.min(100, Math.max(0, (spent / total) * 100))
  const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-indigo-500'

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500">Budget used</span>
        <span className="text-xs font-medium text-gray-700">{pct.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-gray-400">Spent: {fmtCurrency(spent)}</span>
        <span className="text-[10px] text-gray-400">Remaining: {fmtCurrency(total - spent)}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small stat item used in campaigns
// ---------------------------------------------------------------------------

function MiniStat({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
      <Icon size={14} className="text-gray-400 flex-shrink-0" />
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Connection Tab
// ---------------------------------------------------------------------------

function ConnectionTab({ connection }: { connection: ConnectionData }) {
  const queryClient = useQueryClient()

  // State for the setup form (no connection yet)
  const [appId, setAppId] = useState('')
  const [appSecret, setAppSecret] = useState('')

  // State for the OAuth code exchange
  const [authCode, setAuthCode] = useState('')

  // State for account / page selection
  const [selectedAccount, setSelectedAccount] = useState('')
  const [selectedPage, setSelectedPage] = useState('')

  // State for disconnect confirmation
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  // ---- Mutations ----

  const createMutation = useMutation({
    mutationFn: (data: { app_id: string; app_secret: string }) => metaApi.createConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-connection'] })
    },
  })

  const exchangeMutation = useMutation({
    mutationFn: (data: { code: string; redirect_uri: string }) => metaApi.exchangeCode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-connection'] })
    },
  })

  const testMutation = useMutation({
    mutationFn: () => metaApi.testConnection(),
  })

  const syncAdsTrigger = useMutation({
    mutationFn: async () => {
      // Full sync: ads data + attribution recompute
      const syncResult = await metaApi.syncAds()
      await metaAnalyticsApi.recompute()
      return syncResult
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => metaApi.deleteConnection(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-connection'] })
      setConfirmDisconnect(false)
    },
  })

  const toggleTrackingMutation = useMutation({
    mutationFn: (enabled: boolean) => metaApi.toggleAdsTracking({ enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-connection'] })
    },
  })

  // ---- Account / Page queries (only when we have a token but no selection) ----

  const hasToken = !!connection?.access_token
  const needsSelection = hasToken && (!connection?.ad_account_id || !connection?.page_id)

  const { data: accountsData } = useQuery({
    queryKey: ['meta-accounts'],
    queryFn: () => metaApi.listAdAccounts(),
    enabled: needsSelection,
  })

  const { data: pagesData } = useQuery({
    queryKey: ['meta-pages'],
    queryFn: () => metaApi.listPages(),
    enabled: needsSelection,
  })

  const selectMutation = useMutation({
    mutationFn: (data: { ad_account_id: string; ad_account_name: string; page_id: string; page_name: string }) =>
      metaApi.selectAccountAndPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-connection'] })
    },
  })

  const oauthUrlMutation = useMutation({
    mutationFn: () => metaApi.getOAuthUrl(),
  })

  const accounts: any[] = accountsData?.data || []
  const pages: any[] = pagesData?.data || []

  // ---- No connection: show setup form ----

  if (!connection || !connection.id) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Facebook size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Connect Meta Business</h2>
              <p className="text-sm text-gray-500">Enter your Meta App credentials to get started</p>
            </div>
          </div>
          <div className="space-y-4">
            <Input label="App ID" placeholder="Your Meta App ID" value={appId} onChange={(e) => setAppId(e.target.value)} />
            <Input label="App Secret" type="password" placeholder="Your Meta App Secret" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} />
            {createMutation.isError && (
              <p className="text-sm text-red-600">Failed to create connection. Please check your credentials.</p>
            )}
            <Button
              loading={createMutation.isPending}
              disabled={!appId.trim() || !appSecret.trim()}
              onClick={() => createMutation.mutate({ app_id: appId.trim(), app_secret: appSecret.trim() })}
              className="w-full"
            >
              Connect
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ---- Connection exists but no token: show OAuth flow ----

  if (!hasToken) {
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Authorize with Facebook</h2>
              <p className="text-sm text-gray-500">Your app is connected but not yet authorized</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Click the button below to open Facebook's authorization page. After approving, copy the authorization code from the redirect URL and paste it here.
          </p>
          <Button
            variant="primary"
            loading={oauthUrlMutation.isPending}
            onClick={async () => {
              const res = await oauthUrlMutation.mutateAsync()
              const url = res?.data?.url
              if (url) window.open(url, '_blank', 'width=600,height=700')
            }}
            className="w-full mb-4"
          >
            <ExternalLink size={16} />
            Open Facebook Authorization
          </Button>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <Input
              label="Authorization Code"
              placeholder="Paste the code from the redirect URL"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
            />
            {exchangeMutation.isError && (
              <p className="text-sm text-red-600">Failed to exchange code. Make sure you copied the full code and try again.</p>
            )}
            {exchangeMutation.isSuccess && (
              <p className="text-sm text-green-600">Authorization successful!</p>
            )}
            <Button
              loading={exchangeMutation.isPending}
              disabled={!authCode.trim()}
              onClick={() => exchangeMutation.mutate({ code: authCode.trim(), redirect_uri: window.location.origin + '/meta/callback' })}
              className="w-full"
            >
              Submit Code
            </Button>
          </div>
        </div>

        <Button variant="ghost" size="sm" onClick={() => { setConfirmDisconnect(true) }} className="text-red-500">
          Remove connection
        </Button>
        <Modal open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)} title="Disconnect Meta?" size="sm">
          <p className="text-sm text-gray-600 mb-4">This will remove your Meta connection and all synced data configuration. This cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
            <Button variant="danger" size="sm" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>Disconnect</Button>
          </div>
        </Modal>
      </div>
    )
  }

  // ---- Has token but no account/page selected ----

  if (needsSelection) {
    const accountOptions = [{ value: '', label: 'Select an ad account' }, ...accounts.map((a: any) => ({ value: a.id || a.account_id, label: a.name || a.account_id || a.id }))]
    const pageOptions = [{ value: '', label: 'Select a page' }, ...pages.map((p: any) => ({ value: p.id || p.page_id, label: p.name || p.id }))]

    const selectedAccountObj = accounts.find((a: any) => (a.id || a.account_id) === selectedAccount)
    const selectedPageObj = pages.find((p: any) => (p.id || p.page_id) === selectedPage)

    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Layers size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Select Account & Page</h2>
              <p className="text-sm text-gray-500">Choose which ad account and page to use</p>
            </div>
          </div>
          <div className="space-y-4">
            <Select label="Ad Account" options={accountOptions} value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)} />
            <Select label="Facebook Page" options={pageOptions} value={selectedPage} onChange={(e) => setSelectedPage(e.target.value)} />
            {selectMutation.isError && (
              <p className="text-sm text-red-600">Failed to save selection. Please try again.</p>
            )}
            <Button
              loading={selectMutation.isPending}
              disabled={!selectedAccount || !selectedPage}
              onClick={() => selectMutation.mutate({
                ad_account_id: selectedAccount,
                ad_account_name: selectedAccountObj?.name || selectedAccount,
                page_id: selectedPage,
                page_name: selectedPageObj?.name || selectedPage,
              })}
              className="w-full"
            >
              Save Selection
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ---- Fully connected: show status card ----

  const tokenExpiry = connection.token_expires_at ? new Date(connection.token_expires_at) : null
  const tokenValid = tokenExpiry ? tokenExpiry > new Date() : true
  const tokenExpiring = tokenExpiry ? (tokenExpiry.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000) : false
  const statusText = connection.status || 'active'
  const testData = testMutation.data?.data

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Meta Connected</h2>
              <p className="text-sm text-gray-500">Your account is linked and ready</p>
            </div>
          </div>
          <Badge variant={statusText === 'active' ? 'success' : statusText === 'expired' ? 'danger' : 'warning'}>
            {statusText}
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Ad Account</p>
            <p className="text-sm font-medium text-gray-900">{connection.ad_account_name || connection.ad_account_id || 'Not set'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Page</p>
            <p className="text-sm font-medium text-gray-900">{connection.page_name || connection.page_id || 'Not set'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Token Status</p>
            <div className="flex items-center gap-2">
              {tokenValid ? (
                tokenExpiring ? (
                  <>
                    <AlertTriangle size={14} className="text-amber-500" />
                    <span className="text-sm text-amber-700 font-medium">Expiring soon</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={14} className="text-green-500" />
                    <span className="text-sm text-green-700 font-medium">Valid</span>
                  </>
                )
              ) : (
                <>
                  <XCircle size={14} className="text-red-500" />
                  <span className="text-sm text-red-700 font-medium">Expired</span>
                </>
              )}
            </div>
            {tokenExpiry && <p className="text-xs text-gray-400 mt-1">Expires {fmtDate(connection.token_expires_at)}</p>}
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Ads Tracking</p>
            <button
              className="flex items-center gap-2 mt-0.5"
              onClick={() => toggleTrackingMutation.mutate(!connection.ads_tracking_enabled)}
              disabled={toggleTrackingMutation.isPending}
            >
              {connection.ads_tracking_enabled ? (
                <ToggleRight size={24} className="text-indigo-600" />
              ) : (
                <ToggleLeft size={24} className="text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-700">
                {connection.ads_tracking_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </button>
          </div>
        </div>

        {/* Test result banner */}
        {testMutation.isSuccess && testData && (
          <div className={`rounded-lg p-3 mb-4 ${testData.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm font-medium ${testData.valid ? 'text-green-700' : 'text-red-700'}`}>
              {testData.valid ? 'Connection is healthy.' : 'Connection test failed. Your token may be expired.'}
            </p>
            {testData.scopes && testData.scopes.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">Scopes: {testData.scopes.join(', ')}</p>
            )}
          </div>
        )}
        {testMutation.isError && (
          <div className="rounded-lg p-3 mb-4 bg-red-50 border border-red-200">
            <p className="text-sm font-medium text-red-700">Could not reach Meta API. Check your network and try again.</p>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" loading={testMutation.isPending} onClick={() => testMutation.mutate()}>
            <Plug size={14} />
            Test Connection
          </Button>
          <Button variant="primary" size="sm" loading={syncAdsTrigger.isPending} onClick={() => syncAdsTrigger.mutate()}>
            <RefreshCw size={14} />
            Full Sync Now
          </Button>
          <Button variant="danger" size="sm" onClick={() => setConfirmDisconnect(true)}>
            <Unplug size={14} />
            Disconnect
          </Button>
        </div>
        {syncAdsTrigger.isSuccess && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-700">
              Sync complete: {(syncAdsTrigger.data as any)?.data?.campaigns_synced || 0} campaigns,{' '}
              {(syncAdsTrigger.data as any)?.data?.ad_sets_synced || 0} ad sets,{' '}
              {(syncAdsTrigger.data as any)?.data?.insights_synced || 0} insights.
            </p>
          </div>
        )}
        {syncAdsTrigger.isError && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{(syncAdsTrigger.error as any)?.message || 'Sync failed. Check connection and try again.'}</p>
          </div>
        )}
      </div>

      {/* Disconnect confirmation modal */}
      <Modal open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)} title="Disconnect Meta?" size="sm">
        <p className="text-sm text-gray-600 mb-4">
          This will remove your Meta connection, all form mappings, and sync settings. Leads already imported will remain in the CRM.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
          <Button variant="danger" size="sm" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>Disconnect</Button>
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lead Forms Tab
// ---------------------------------------------------------------------------

function LeadFormsTab() {
  const queryClient = useQueryClient()
  const [mappingFormId, setMappingFormId] = useState<string | null>(null)
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({})
  const [autoSync, setAutoSync] = useState(true)
  const [syncInterval, setSyncInterval] = useState('')

  // ---- Queries ----

  const { data: formsData, isLoading: formsLoading } = useQuery({
    queryKey: ['meta-forms'],
    queryFn: () => metaApi.listForms(),
  })

  const { data: syncStatusData } = useQuery({
    queryKey: ['meta-sync-status'],
    queryFn: () => metaApi.getSyncStatus(),
  })

  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: ['meta-form-questions', mappingFormId],
    queryFn: () => metaApi.getFormQuestions(mappingFormId!),
    enabled: !!mappingFormId,
  })

  // Initialize sync interval from server
  useEffect(() => {
    if (syncStatusData?.data?.sync_interval_minutes != null) {
      setSyncInterval(String(syncStatusData.data.sync_interval_minutes))
    }
  }, [syncStatusData])

  // Initialize field mapping — prefer saved mapping over suggested
  useEffect(() => {
    if (mappingFormId) {
      const allForms: any[] = formsData?.data || []
      const form = allForms.find((f: any) => String(f.form_id ?? f.id) === mappingFormId)
      if (form?.field_mapping && Object.keys(form.field_mapping).length > 0) {
        setFieldMapping(form.field_mapping)
      } else if (questionsData?.data?.suggested_mapping) {
        setFieldMapping(questionsData.data.suggested_mapping)
      }
    }
  }, [questionsData, mappingFormId, formsData])

  // ---- Mutations ----

  const syncAllMutation = useMutation({
    mutationFn: () => metaApi.syncAllLeads(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status'] })
      queryClient.invalidateQueries({ queryKey: ['meta-forms'] })
    },
  })

  const [previewFormId, setPreviewFormId] = useState<string | null>(null)

  const syncFormMutation = useMutation({
    mutationFn: (formId: string) => metaApi.syncFormLeads(formId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status'] })
      queryClient.invalidateQueries({ queryKey: ['meta-forms'] })
    },
  })

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['meta-lead-preview', previewFormId],
    queryFn: () => metaApi.previewFormLeads(previewFormId!),
    enabled: !!previewFormId,
  })

  const scheduleMutation = useMutation({
    mutationFn: (data: { sync_interval_minutes: number | null; enabled: boolean }) => metaApi.setSyncSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status'] })
    },
  })

  const saveMappingMutation = useMutation({
    mutationFn: ({ formId, data }: { formId: string; data: { field_mapping: Record<string, string>; auto_sync_enabled: boolean } }) =>
      metaApi.saveFormMapping(formId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-forms'] })
      setMappingFormId(null)
    },
  })

  const forms: any[] = formsData?.data || []
  const syncStatus = syncStatusData?.data
  const questions: any[] = questionsData?.data?.questions || []

  if (formsLoading) {
    return <LoadingState message="Loading lead forms..." />
  }

  return (
    <div className="space-y-6">
      {/* Sync controls bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Lead Sync</h2>
            {syncStatus && (
              <p className="text-sm text-gray-500 mt-0.5">
                {(syncStatus.forms as any[])?.length || 0} forms tracked
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">Auto-sync:</label>
              <Select
                options={SYNC_INTERVALS}
                value={syncInterval}
                onChange={(e) => {
                  const val = e.target.value
                  setSyncInterval(val)
                  scheduleMutation.mutate({
                    sync_interval_minutes: val ? Number(val) : null,
                    enabled: !!val,
                  })
                }}
                className="!w-44"
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              loading={syncAllMutation.isPending}
              onClick={() => syncAllMutation.mutate()}
            >
              <RefreshCw size={14} />
              Sync All Leads Now
            </Button>
          </div>
        </div>

        {syncAllMutation.isSuccess && syncAllMutation.data?.data && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-700">
              Synced {syncAllMutation.data.data.forms_synced} forms, imported {syncAllMutation.data.data.total_new_leads} new leads.
            </p>
          </div>
        )}
        {syncAllMutation.isError && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">
              Lead sync failed: {(syncAllMutation.error as Error).message || 'Unknown error'}
            </p>
          </div>
        )}
      </div>

      {/* Forms list */}
      {forms.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No Lead Forms Found"
          description="We could not find any lead gen forms on your connected Facebook Page. Create a lead form in Meta Ads Manager first."
        />
      ) : (
        <div className="space-y-3">
          {forms.map((form: any) => {
            const formId = String(form.form_id || form.id)
            const isMappingOpen = mappingFormId === formId
            return (
              <div key={formId} className="bg-white rounded-xl border border-gray-200">
                <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText size={16} className="text-gray-400 flex-shrink-0" />
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{form.name || form.form_name || formId}</h3>
                      <Badge variant={form.status === 'ACTIVE' ? 'success' : 'default'}>
                        {form.status || 'unknown'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500">
                      {form.leads_count != null && <span className="font-medium text-gray-700">{fmt(form.leads_count)} leads in Meta</span>}
                      {form.total_leads_synced > 0 && <span>{fmt(form.total_leads_synced)} synced to CRM</span>}
                      {Number(form.organic_leads_count) > 0 && <span>{fmt(form.organic_leads_count)} organic</span>}
                      {Number(form.expired_leads_count) > 0 && <span className="text-amber-600">{fmt(form.expired_leads_count)} expired</span>}
                      {form.created_time && (
                        <span className="flex items-center gap-1">
                          <Calendar size={11} />
                          Created {fmtShortDate(form.created_time)}
                        </span>
                      )}
                      {form.last_synced_at && (
                        <span className="flex items-center gap-1">
                          <Clock size={11} />
                          Last synced {fmtDate(form.last_synced_at)}
                        </span>
                      )}
                      {form.auto_sync_enabled != null && (
                        <span>Auto-sync: {form.auto_sync_enabled ? 'On' : 'Off'}</span>
                      )}
                      {form.locale && <span>{form.locale}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewFormId(previewFormId === formId ? null : formId)}
                    >
                      <Eye size={14} />
                      Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={syncFormMutation.isPending && syncFormMutation.variables === formId}
                      onClick={() => syncFormMutation.mutate(formId)}
                    >
                      <RefreshCw size={14} />
                      Sync
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setMappingFormId(isMappingOpen ? null : formId)}
                    >
                      <ArrowRightLeft size={14} />
                      {isMappingOpen ? 'Close Mapping' : 'Configure Mapping'}
                    </Button>
                  </div>
                </div>

                {/* Inline mapping editor */}
                {isMappingOpen && (
                  <div className="border-t border-gray-100 p-5 bg-gray-50/50">
                    {questionsLoading ? (
                      <p className="text-sm text-gray-400 text-center py-4">Loading form fields...</p>
                    ) : questions.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No fields found for this form.</p>
                    ) : (
                      <>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Field Mapping</h4>
                        <p className="text-sm text-gray-500 mb-4">Map each Meta form field to a CRM field. Unmapped fields will be skipped.</p>
                        <div className="space-y-3">
                          {questions.map((q: any) => {
                            const key = q.key || q.field_key || q.id
                            return (
                              <div key={key} className="flex items-center gap-4">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-700 truncate">{q.label || q.question || key}</p>
                                  {q.type && <p className="text-xs text-gray-400">{q.type}</p>}
                                </div>
                                <div className="text-gray-300">
                                  <ArrowRightLeft size={14} />
                                </div>
                                <div className="flex-1">
                                  <Select
                                    options={SYSTEM_FIELDS}
                                    value={fieldMapping[key] || ''}
                                    onChange={(e) => setFieldMapping((prev) => ({ ...prev, [key]: e.target.value }))}
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>

                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-200">
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={autoSync}
                              onChange={(e) => setAutoSync(e.target.checked)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            Enable auto-sync for this form
                          </label>
                          <div className="flex items-center gap-2">
                            <Button variant="secondary" size="sm" onClick={() => setMappingFormId(null)}>Cancel</Button>
                            <Button
                              size="sm"
                              loading={saveMappingMutation.isPending}
                              onClick={() => {
                                const cleaned: Record<string, string> = {}
                                for (const [k, v] of Object.entries(fieldMapping)) {
                                  if (v) cleaned[k] = v
                                }
                                saveMappingMutation.mutate({
                                  formId,
                                  data: { field_mapping: cleaned, auto_sync_enabled: autoSync },
                                })
                              }}
                            >
                              <Save size={14} />
                              Save Mapping
                            </Button>
                          </div>
                        </div>

                        {saveMappingMutation.isError && (
                          <p className="text-sm text-red-600 mt-2">Failed to save mapping. Please try again.</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Lead Preview Modal */}
              </div>
            )
          })}
        </div>
      )}

      {/* Lead Preview Modal */}
      {previewFormId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={(e) => { if (e.target === e.currentTarget) setPreviewFormId(null) }}>
          <div className="fixed inset-0 bg-black/40" />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Lead Preview</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {previewData?.data?.form_name || 'Loading...'} — preview only, not imported
                </p>
              </div>
              <button onClick={() => setPreviewFormId(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <XCircle size={20} />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-6">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <RefreshCw size={20} className="animate-spin mr-2" /> Fetching leads from Meta...
                </div>
              ) : !previewData?.data?.leads || previewData.data.leads.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-12">No leads found in this form.</p>
              ) : (() => {
                const leads = previewData.data.leads
                const fieldKeys = Object.keys(leads[0]?.mapped_fields || leads[0]?.raw_fields || {})
                return (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-sm text-gray-700 font-medium">{leads.length} leads shown</span>
                      {!previewData.data.has_mapping && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">No field mapping — showing raw Meta field names</span>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                            {fieldKeys.map(k => (
                              <th key={k} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                {k.replace(/_/g, ' ').replace(/\?/g, '')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {leads.map((lead: any, idx: number) => (
                            <tr key={lead.meta_lead_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-xs text-gray-400">{idx + 1}</td>
                              <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                {lead.created_time ? new Date(lead.created_time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
                              </td>
                              {fieldKeys.map(k => (
                                <td key={k} className="px-3 py-2 text-sm text-gray-700 max-w-xs truncate" title={lead.mapped_fields?.[k] || lead.raw_fields?.[k] || ''}>
                                  {lead.mapped_fields?.[k] || lead.raw_fields?.[k] || '--'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Campaigns Tab
// ---------------------------------------------------------------------------

function CampaignsTab() {
  const queryClient = useQueryClient()
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null)
  const [expandedAdSet, setExpandedAdSet] = useState<string | null>(null)

  // ---- Queries ----

  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ['meta-campaigns'],
    queryFn: () => metaApi.listCampaigns(),
  })

  const syncAdsMutation = useMutation({
    mutationFn: () => metaApi.syncAds(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['meta-adsets'] })
      queryClient.invalidateQueries({ queryKey: ['meta-ads'] })
      queryClient.invalidateQueries({ queryKey: ['meta-campaign-insights'] })
      queryClient.invalidateQueries({ queryKey: ['meta-adset-insights'] })
      queryClient.invalidateQueries({ queryKey: ['meta-ad-insights'] })
    },
  })

  const campaigns: any[] = campaignsData?.data || []

  if (campaignsLoading) {
    return <LoadingState message="Loading campaigns..." />
  }

  return (
    <div className="space-y-6">
      {/* Sync bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Ad Campaigns</h2>
          <p className="text-sm text-gray-500 mt-0.5">{campaigns.length} campaigns found</p>
        </div>
        <Button variant="primary" size="sm" loading={syncAdsMutation.isPending} onClick={() => syncAdsMutation.mutate()}>
          <RefreshCw size={14} />
          Sync Ads
        </Button>
      </div>

      {syncAdsMutation.isSuccess && syncAdsMutation.data?.data && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-700">
            Synced {syncAdsMutation.data.data.campaigns_synced} campaigns with {syncAdsMutation.data.data.insights_synced} insight records.
          </p>
        </div>
      )}

      {syncAdsMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">
            {(syncAdsMutation.error as any)?.message || 'Failed to sync ads. Check your Meta connection and try again.'}
          </p>
        </div>
      )}

      {/* Campaign list */}
      {campaigns.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No Campaigns Found"
          description="No ad campaigns have been synced yet. Click 'Sync Ads' to pull campaigns from your Meta ad account."
        />
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign: any) => {
            const cId = String(campaign.campaign_id || campaign.id)
            const isExpanded = expandedCampaign === cId
            return (
              <CampaignCard
                key={cId}
                campaign={campaign}
                campaignId={cId}
                isExpanded={isExpanded}
                onToggle={() => setExpandedCampaign(isExpanded ? null : cId)}
                expandedAdSet={expandedAdSet}
                onToggleAdSet={(id) => setExpandedAdSet(expandedAdSet === id ? null : id)}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Campaign Card (with lazy-loaded ad sets)
// ---------------------------------------------------------------------------

function CampaignCard({
  campaign,
  campaignId,
  isExpanded,
  onToggle,
  expandedAdSet,
  onToggleAdSet,
}: {
  campaign: any
  campaignId: string
  isExpanded: boolean
  onToggle: () => void
  expandedAdSet: string | null
  onToggleAdSet: (id: string) => void
}) {
  const { data: adSetsData, isLoading: adSetsLoading } = useQuery({
    queryKey: ['meta-adsets', campaignId],
    queryFn: () => metaApi.listAdSets(campaignId),
    enabled: isExpanded,
  })

  const { data: insightsData } = useQuery({
    queryKey: ['meta-campaign-insights', campaignId],
    queryFn: () => metaApi.getCampaignInsights(campaignId),
    enabled: isExpanded,
  })

  const adSets: any[] = adSetsData?.data || []
  const insightsArray: any[] = Array.isArray(insightsData?.data) ? insightsData.data : []
  const status = campaign.status || campaign.effective_status || 'UNKNOWN'

  // Use campaign-level aggregate totals (from the campaigns list query)
  const totalImpressions = campaign.total_impressions
  const totalClicks = campaign.total_clicks
  const totalSpend = campaign.total_spend
  const totalConversions = campaign.total_conversions
  const campaignCtr = campaign.ctr
  const campaignCpl = campaign.cost_per_lead

  const hasTotals = totalImpressions != null || totalClicks != null || totalSpend != null

  // Budget calculations
  const dailyBudget = Number(campaign.daily_budget) || 0
  const lifetimeBudget = Number(campaign.lifetime_budget) || 0
  const budgetRemaining = Number(campaign.budget_remaining) || 0
  const spent = Number(totalSpend) || 0
  const budgetTotal = lifetimeBudget > 0 ? lifetimeBudget : (budgetRemaining > 0 ? spent + budgetRemaining : 0)

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Campaign header */}
      <button
        className="w-full p-5 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isExpanded ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
          <Megaphone size={16} className="text-indigo-500 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{campaign.name || campaign.campaign_name || campaignId}</h3>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {campaign.objective && (
                <span className="text-xs text-gray-400">{campaign.objective}</span>
              )}
              {hasTotals && (
                <span className="text-xs text-gray-400">
                  {fmt(totalImpressions)} impressions / {fmtCurrency(totalSpend)} spent
                </span>
              )}
            </div>
          </div>
        </div>
        <Badge variant={campaignStatusVariant(status)}>{status}</Badge>
      </button>

      {/* Expanded: info bar + metrics + sparkline + ad sets */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Info bar */}
          <div className="p-4 bg-gray-50/50 border-b border-gray-100">
            <div className="flex items-center gap-4 flex-wrap text-xs text-gray-600">
              {campaign.start_time && (
                <span className="flex items-center gap-1">
                  <Calendar size={12} className="text-gray-400" />
                  Started {fmtShortDate(campaign.start_time)}
                </span>
              )}
              {(campaign.start_time || campaign.stop_time) && (
                <span className="flex items-center gap-1">
                  <Clock size={12} className="text-gray-400" />
                  {campaign.stop_time
                    ? `Ended ${fmtShortDate(campaign.stop_time)} (${daysBetween(campaign.start_time, campaign.stop_time)})`
                    : daysBetween(campaign.start_time, null)
                  }
                </span>
              )}
              {campaign.objective && (
                <Badge variant="default">{campaign.objective}</Badge>
              )}
              {campaign.buying_type && (
                <span className="flex items-center gap-1">
                  <Wallet size={12} className="text-gray-400" />
                  {campaign.buying_type}
                </span>
              )}
              {campaign.bid_strategy && (
                <span className="flex items-center gap-1">
                  <Target size={12} className="text-gray-400" />
                  {campaign.bid_strategy.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>

          {/* Budget section */}
          {(dailyBudget > 0 || lifetimeBudget > 0 || budgetRemaining > 0) && (
            <div className="p-4 bg-white border-b border-gray-100">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Budget</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                {dailyBudget > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Daily Budget</p>
                    <p className="text-sm font-semibold text-gray-900">{fmtCurrency(dailyBudget)}</p>
                  </div>
                )}
                {lifetimeBudget > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Lifetime Budget</p>
                    <p className="text-sm font-semibold text-gray-900">{fmtCurrency(lifetimeBudget)}</p>
                  </div>
                )}
                {budgetRemaining > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Remaining</p>
                    <p className="text-sm font-semibold text-gray-900">{fmtCurrency(budgetRemaining)}</p>
                  </div>
                )}
              </div>
              {budgetTotal > 0 && (
                <BudgetProgress spent={spent} total={budgetTotal} />
              )}
            </div>
          )}

          {/* Aggregate metrics from campaign totals */}
          {hasTotals && (
            <div className="p-4 bg-gray-50/50 border-b border-gray-100">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Performance Summary</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-2">
                <MiniStat icon={Eye} label="Impressions" value={fmt(totalImpressions)} />
                <MiniStat icon={MousePointerClick} label="Clicks" value={fmt(totalClicks)} />
                <MiniStat icon={TrendingUp} label="CTR" value={fmtPct(campaignCtr)} />
                <MiniStat icon={DollarSign} label="Total Spend" value={fmtCurrency(totalSpend)} />
                <MiniStat icon={BarChart3} label="Conversions" value={fmt(totalConversions)} />
                <MiniStat icon={Users} label="Cost per Lead" value={campaignCpl != null ? fmtCurrency(campaignCpl) : '--'} />
                <MiniStat icon={DollarSign} label="Budget Left" value={budgetRemaining > 0 ? fmtCurrency(budgetRemaining) : '--'} />
              </div>

              {/* Spend sparkline from daily insights */}
              <SpendSparkline data={insightsArray} valueKey="spend" />
            </div>
          )}

          {/* If no campaign totals but we have insights array, show from first record as fallback */}
          {!hasTotals && insightsArray.length > 0 && (
            <div className="p-4 bg-gray-50/50 border-b border-gray-100">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Performance Summary</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                <MiniStat icon={Eye} label="Impressions" value={fmt(insightsArray[0]?.impressions)} />
                <MiniStat icon={MousePointerClick} label="Clicks" value={fmt(insightsArray[0]?.clicks)} />
                <MiniStat icon={TrendingUp} label="CTR" value={fmtPct(insightsArray[0]?.ctr)} />
                <MiniStat icon={DollarSign} label="Spend" value={fmtCurrency(insightsArray[0]?.spend)} />
                <MiniStat icon={DollarSign} label="CPC" value={fmtCurrency(insightsArray[0]?.cpc)} />
                <MiniStat icon={BarChart3} label="Conversions" value={fmt(insightsArray[0]?.conversions || 0)} />
              </div>
              <SpendSparkline data={insightsArray} valueKey="spend" />
            </div>
          )}

          {/* Ad sets */}
          <div className="p-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Ad Sets</h4>
            {adSetsLoading ? (
              <p className="text-sm text-gray-400 text-center py-4">Loading ad sets...</p>
            ) : adSets.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No ad sets found for this campaign.</p>
            ) : (
              <div className="space-y-2">
                {adSets.map((adSet: any) => {
                  const asId = String(adSet.ad_set_id || adSet.id)
                  const asExpanded = expandedAdSet === asId
                  return (
                    <AdSetCard
                      key={asId}
                      adSet={adSet}
                      adSetId={asId}
                      isExpanded={asExpanded}
                      onToggle={() => onToggleAdSet(asId)}
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ad Set Card (with lazy-loaded ads)
// ---------------------------------------------------------------------------

function AdSetCard({
  adSet,
  adSetId,
  isExpanded,
  onToggle,
}: {
  adSet: any
  adSetId: string
  isExpanded: boolean
  onToggle: () => void
}) {
  const { data: adsData, isLoading: adsLoading } = useQuery({
    queryKey: ['meta-ads', adSetId],
    queryFn: () => metaApi.listAds(adSetId),
    enabled: isExpanded,
  })

  const { data: insightsData } = useQuery({
    queryKey: ['meta-adset-insights', adSetId],
    queryFn: () => metaApi.getAdSetInsights(adSetId),
    enabled: isExpanded,
  })

  const ads: any[] = adsData?.data || []
  const insights: any = (insightsData?.data as any)?.[0] || insightsData?.data || {}
  const status = adSet.status || adSet.effective_status || 'UNKNOWN'

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isExpanded ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />}
          <Layers size={14} className="text-violet-500 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-gray-800 truncate">{adSet.name || adSet.adset_name || adSetId}</h4>
            <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
              {adSet.daily_budget && <span>Budget: {fmtCurrency(adSet.daily_budget)}/day</span>}
              {adSet.lifetime_budget && <span>Lifetime: {fmtCurrency(adSet.lifetime_budget)}</span>}
              {adSet.optimization_goal && <span className="text-violet-500">{adSet.optimization_goal.replace(/_/g, ' ')}</span>}
              {adSet.billing_event && <span>Billed per {adSet.billing_event.toLowerCase()}</span>}
              {adSet.start_time && <span>{fmtShortDate(adSet.start_time)}{adSet.end_time ? ` — ${fmtShortDate(adSet.end_time)}` : ' — ongoing'}</span>}
            </div>
          </div>
        </div>
        <Badge variant={campaignStatusVariant(status)}>{status}</Badge>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Ad set insights */}
          {Object.keys(insights).length > 0 && (
            <div className="p-3 bg-gray-50/50">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MiniStat icon={Eye} label="Impressions" value={fmt(insights.impressions)} />
                <MiniStat icon={MousePointerClick} label="Clicks" value={fmt(insights.clicks)} />
                <MiniStat icon={TrendingUp} label="CTR" value={fmtPct(insights.ctr)} />
                <MiniStat icon={DollarSign} label="Spend" value={fmtCurrency(insights.spend)} />
              </div>
            </div>
          )}

          {/* Ads list */}
          <div className="p-3">
            <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Ads</h5>
            {adsLoading ? (
              <p className="text-xs text-gray-400 text-center py-3">Loading ads...</p>
            ) : ads.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-3">No ads found in this ad set.</p>
            ) : (
              <div className="space-y-1.5">
                {ads.map((ad: any) => (
                  <AdRow key={ad.ad_id || ad.id} ad={ad} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ad Row (single ad with lazy insights)
// ---------------------------------------------------------------------------

function AdRow({ ad }: { ad: any }) {
  const [showInsights, setShowInsights] = useState(false)
  const adId = String(ad.ad_id || ad.id)

  const { data: insightsData, isLoading: insightsLoading } = useQuery({
    queryKey: ['meta-ad-insights', adId],
    queryFn: () => metaApi.getAdInsights(adId),
    enabled: showInsights,
  })

  const insights: any = (insightsData?.data as any)?.[0] || insightsData?.data || {}
  const status = ad.status || ad.effective_status || 'UNKNOWN'

  return (
    <div className="border border-gray-100 rounded-lg bg-white">
      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {ad.thumbnail_url ? (
            <img src={ad.thumbnail_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 border border-gray-200" />
          ) : (
            <Image size={14} className="text-pink-500 flex-shrink-0" />
          )}
          <span className="text-sm text-gray-800 truncate">{ad.name || ad.ad_name || adId}</span>
          <Badge variant={campaignStatusVariant(status)}>{status}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {ad.preview_url && (
            <a href={ad.preview_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-indigo-600">
              <ExternalLink size={13} />
            </a>
          )}
          <button
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            onClick={() => setShowInsights(!showInsights)}
          >
            {showInsights ? 'Hide' : 'Insights'}
          </button>
        </div>
      </div>
      {showInsights && (
        <div className="border-t border-gray-50 px-3 py-2 bg-gray-50/50">
          {insightsLoading ? (
            <p className="text-xs text-gray-400 text-center py-2">Loading...</p>
          ) : Object.keys(insights).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              <MiniStat icon={Eye} label="Impressions" value={fmt(insights.impressions)} />
              <MiniStat icon={MousePointerClick} label="Clicks" value={fmt(insights.clicks)} />
              <MiniStat icon={TrendingUp} label="CTR" value={fmtPct(insights.ctr)} />
              <MiniStat icon={DollarSign} label="Spend" value={fmtCurrency(insights.spend)} />
              <MiniStat icon={DollarSign} label="CPC" value={insights.cpc != null ? fmtCurrency(insights.cpc) : '--'} />
              <MiniStat icon={Users} label="CPL" value={insights.cost_per_lead != null ? fmtCurrency(insights.cost_per_lead) : '--'} />
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-2">No insight data available for this ad.</p>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function MetaAdsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('connection')

  // Fetch connection to determine default tab
  const { data: connectionData, isLoading: connectionLoading } = useQuery({
    queryKey: ['meta-connection'],
    queryFn: () => metaApi.getConnection(),
  })

  const connection: ConnectionData = connectionData?.data as ConnectionData

  const isConnected = !!(connection?.access_token && connection?.ad_account_id && connection?.page_id)

  // Set initial tab based on connection state
  useEffect(() => {
    if (!connectionLoading) {
      if (isConnected) {
        setActiveTab('forms')
      } else {
        setActiveTab('connection')
      }
    }
  }, [connectionLoading, isConnected])

  const tabs: { key: Tab; label: string; icon: any; enabled: boolean }[] = [
    { key: 'connection', label: 'Connection', icon: Plug, enabled: true },
    { key: 'forms', label: 'Lead Forms', icon: FileText, enabled: isConnected },
    { key: 'campaigns', label: 'Campaigns', icon: BarChart3, enabled: isConnected },
  ]

  if (connectionLoading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Meta Ads</h1>
        <LoadingState message="Loading Meta connection..." />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Meta Ads</h1>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            disabled={!tab.enabled}
            onClick={() => tab.enabled && setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.key
                ? 'border-indigo-600 text-indigo-600'
                : tab.enabled
                ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                : 'border-transparent text-gray-300 cursor-not-allowed'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'connection' && (
        <ConnectionTab connection={connection} />
      )}
      {activeTab === 'forms' && isConnected && <LeadFormsTab />}
      {activeTab === 'campaigns' && isConnected && <CampaignsTab />}
    </div>
  )
}
